import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsColorOrReference } from '@builder4/shared/dto/styles/validators/IsColorOrReference';
import { IsRgbaOrReference } from '@builder4/shared/dto/styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
  InteractionEventDto,
  ShadowSettingsDto,
  SHADOW_TYPE_VALUES,
  type ShadowType,
} from '@builder4/shared/dto/craft/props/shared';
import { IsTRBLNumberCoords } from '@builder4/shared/dto/craft/validators/IsTRBLNumberCoords';
import { GradientDto, RgbaDto } from '@builder4/shared/dto/styles';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
  IMAGE_CONTINUOUS_TYPES,
} from '@builder4/shared/types/animation';

const FIT_TYPE_VALUES = ['contain', 'cover', 'fill', 'none', 'scale-down', 'tile'] as const;
const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;
const FILL_SPACE_VALUES = ['yes', 'no'] as const;
const MAX_WIDTH_UNIT_VALUES = ['px', '%'] as const;
const BACKGROUND_POSITION_VALUES = [
  'left top',
  'center top',
  'right top',
  'left center',
  'center center',
  'right center',
  'left bottom',
  'center bottom',
  'right bottom',
] as const;

/**
 * Breakpoint-aware properties for Image element.
 * Derived from getDetachablePropsForNode('Image'):
 * - DETACHABLE_PROPS_REGISTRY: width, height, fillSpace, flexBasis
 * - POSITION_PROPS: position, positionAnchorsState, left, right, top, bottom, zIndex
 * - BOUNDING_BOX_PROPS: padding, margin, borderRadius
 * - MAX_WIDTH_PROPS: maxWidth, maxWidthUnit
 */
export class ImageBreakpointPropsDto {
  // From DETACHABLE_PROPS_REGISTRY[IMAGE_NODE_NAME]
  @IsOptional()
  @IsString()
  width?: string;

  @IsOptional()
  @IsString()
  height?: string;

  @IsOptional()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace?: (typeof FILL_SPACE_VALUES)[number];

  @IsOptional()
  @IsString()
  flexBasis?: string;

  // From BOUNDING_BOX_PROPS
  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  // From POSITION_PROPS
  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  // From MAX_WIDTH_PROPS
  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];
}

export class ImagePropsDto {
  @IsOptional()
  @IsString()
  id?: string;

  @IsDefined()
  @IsString()
  src!: string;

  @IsDefined()
  @IsString()
  alt!: string;

  // Sizing
  @IsDefined()
  @IsString()
  width!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minWidth!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minHeight!: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];

  // Image styling
  @IsDefined()
  @IsIn([...FIT_TYPE_VALUES])
  fitType!: (typeof FIT_TYPE_VALUES)[number];

  @IsDefined()
  @IsString()
  objectPosition!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  @Max(1)
  opacity!: number;

  // Background (for tile mode)
  @IsOptional()
  @IsColorOrReference()
  backgroundColor?: RgbaDto | GradientDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => GradientDto)
  bgGradient?: GradientDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  bgSolid?: RgbaDto;

  @IsDefined()
  @IsString()
  backgroundSize!: string;

  @IsDefined()
  @IsIn([...BACKGROUND_POSITION_VALUES])
  backgroundPosition!: (typeof BACKGROUND_POSITION_VALUES)[number];

  // Spacing
  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  margin!: [number, number, number, number];

  // Borders
  @IsOptional()
  @IsRgbaOrReference()
  borderColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  hoverBorderColor?: RgbaDto | string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  borderWidth!: number;

  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  // Position
  @IsDefined()
  @IsIn([...POSITION_VALUES])
  position!: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex!: number;

  // Visibility
  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  // Flex
  @IsDefined()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace!: (typeof FILL_SPACE_VALUES)[number];

  @IsDefined()
  @IsString()
  flexBasis!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  flexGrow!: number;

  // Aspect ratio
  @IsDefined()
  @IsBoolean()
  aspectRatioLock!: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  aspectRatio?: number;

  // Interaction
  @IsOptional()
  @ValidateNested()
  @Type(() => InteractionEventDto)
  clickEvent?: InteractionEventDto;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;

  // Image continuous animation
  @IsOptional()
  @IsIn([...IMAGE_CONTINUOUS_TYPES])
  continuousAnimation?: (typeof IMAGE_CONTINUOUS_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(30)
  continuousSpeed?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(50)
  continuousIntensity?: number;
  // Shadow
  @IsOptional()
  @IsIn([...SHADOW_TYPE_VALUES])
  shadowType?: ShadowType;

  @IsOptional()
  @ValidateNested()
  @Type(() => ShadowSettingsDto)
  shadowSettings?: ShadowSettingsDto;

  // Breakpoint overrides
  @IsOptional()
  @ValidateNested()
  @Type(() => ImageBreakpointPropsDto)
  tablet?: ImageBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => ImageBreakpointPropsDto)
  mobile?: ImageBreakpointPropsDto;
}
